import { motion } from "framer-motion";
import { Award, Eye, Handshake, Smartphone, Users } from "lucide-react";
import { Button } from "../ui/button";

const REASONS = [
  {
    num: "01",
    title: "MEET FIRA & THE TEAM",
    desc: "Peluang untuk bertemu, berinteraksi dan berkongsi pengalaman secara bersemuka bersama Fira dan team.",
    Icon: Users,
  },
  {
    num: "02",
    title: "VIP APPRECIATION & RECOGNITION",
    desc: "Sebuah penghargaan khas buat komuniti VIP yang menjadi sebahagian daripada perjalanan Fira.",
    Icon: Award,
  },
  {
    num: "03",
    title: "CONNECT WITH THE FIRA FAMILY",
    desc: "Kenali dan berjumpa dengan rakan trader yang selama ini hanya berhubung secara online.",
    Icon: Handshake,
  },
  {
    num: "04",
    title: "WITNESS THE NEW ERA",
    desc: "Jadilah antara yang pertama menyaksikan hala tuju dan pengumuman baharu Fira Worldwide secara langsung.",
    Icon: Eye,
  },
  {
    num: "05",
    title: "FIRA ONE (1 MONTH FREE ACCESS)",
    desc: "Jadilah antara yang pertama menikmati ecosystem Fira Worldwide dengan apps Fira One yang bakal membentuk trader yang sebenar.",
    Icon: Smartphone,
  },
];

const cardStyle = (i) => {
  if (i === 4)
    return "pink-glow group relative overflow-hidden rounded-3xl border border-fira-pink/40 bg-gradient-to-br from-fira-pink/15 via-fira-panel to-fira-deep p-8 transition-all duration-300 hover:border-fira-pink/60 md:col-span-2 md:p-12 h-[26rem] md:h-[22rem]";
  return "group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-fira-pink/40";
};

export const WhyAttend = ({ onReserve }) => (
  <section
    id="why-attend"
    data-testid="why-attend-section"
    className="border-y border-white/5 bg-fira-panel py-24 md:py-32"
  >
    <div className="mx-auto max-w-6xl px-5">
      <div className="text-center">
        <span className="eyebrow">
          Why You Should Attend
        </span>
        <h2 className="font-display mx-auto mt-4 max-w-3xl text-4xl font-semibold leading-tight md:text-5xl">
          One Event. One Family. <span className="italic text-fira-rose">One Unforgettable Experience.</span>
        </h2>
      </div>
      <div className="mt-14 grid gap-5 md:grid-cols-2" data-testid="reasons-grid">
        {REASONS.map(({ num, title, desc, Icon }, i) => (
          <motion.div
            key={num}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`reason-${num}`}
            className={cardStyle(i)}
          >
            {i !== 4 && (
              <span className="font-display pointer-events-none absolute -right-2 -top-7 select-none text-[6.5rem] font-bold italic leading-none text-white/[0.05] transition-colors duration-300 group-hover:text-fira-pink/10">
                {num}
              </span>
            )}
            {i === 4 ? (
              <div className="relative h-full">
                <div className="pointer-events-none absolute -bottom-8 right-[-14%] z-0 h-[14rem] overflow-visible sm:right-[-10%] md:-bottom-12 md:h-[17rem] md:right-[-8%] lg:h-[19rem] lg:right-[-6%]">
                  <img
                    src="/fira-one-app.png"
                    alt="Fira One app"
                    data-testid="fira-one-mockup"
                    className="phone-fade relative h-[26rem] w-auto origin-bottom-right rotate-[8deg] md:h-[32rem] lg:h-[36rem]"
                  />
                </div>
                <div className="pointer-events-none absolute bottom-6 right-10 z-0 h-48 w-48 rounded-full bg-fira-pink/25 blur-3xl md:h-64 md:w-64" />
                <div className="firaone-overlay pointer-events-none absolute inset-0 z-[1]" />
                <div className="absolute left-0 top-1/2 z-10 max-w-[15rem] -translate-y-1/2 sm:max-w-xs md:max-w-md lg:max-w-xl">
                  <h3 className="text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white">
                    Fira One
                    <br />
                    <span className="text-fira-rose">1 Month Free Access</span>
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60 md:text-base">{desc}</p>
                  <div className="mt-5 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 py-2 pl-2 pr-5 backdrop-blur-sm">
                    <span className="btn-lux rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white">
                      Free
                    </span>
                    <span className="text-xs font-medium text-white/70">
                      Akses 1 bulan untuk semua kategori seat
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-fira-pink/40 bg-fira-pink/10">
                    <Icon className="h-5 w-5 text-fira-rose" />
                  </div>
                  <span className="font-display text-2xl font-semibold italic text-fira-pink/50">
                    {num}
                  </span>
                </div>
                <h3 className="mt-5 text-sm font-bold tracking-wider text-white">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{desc}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
      <div className="mt-12 flex justify-center">
        <Button
          onClick={onReserve}
          data-testid="why-attend-cta-btn"
          className="btn-lux rounded-full px-10 py-6 text-sm font-bold tracking-widest text-white transition-all duration-300 hover:scale-105"
        >
          I WANT TO BE THERE
        </Button>
      </div>
    </div>
  </section>
);
